import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db, OperationType, handleFirestoreError } from '../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { signInWithGoogle } from '../services/authService';
import { safeStorage } from '../utils/storage';

export interface AppUser {
  uid: string;
  email: string | null;
  displayName?: string;
  photoURL?: string | null;
}

export const AuthContext = createContext<{
  user: AppUser | null;
  profile: any;
  loading: boolean;
  isAdmin: boolean;
  logOut: () => Promise<void>;
  signIn: (provider: 'google') => Promise<void>;
  adminSignIn: (user: string, pass: string) => Promise<void>;
  signUp: (email: string, name: string, address: string, phone: string, type: string) => Promise<void>;
}>({
  user: null,
  profile: null,
  loading: true,
  isAdmin: false,
  logOut: async () => {},
  signIn: async () => {},
  adminSignIn: async () => {},
  signUp: async () => {}
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<AppUser | null>(() => {
    if (typeof window !== 'undefined' && safeStorage.getItem<string>('he_admin_session', 'false') === 'true') {
      const savedEmail = safeStorage.getItem<string>('he_admin_email', 'hamimenterprises00@gmail.com');
      return { uid: 'admin-manual', email: savedEmail, displayName: 'Hamim Admin (Owner)' };
    }
    return null;
  });
  const [profile, setProfile] = useState<any>(() => {
    if (typeof window !== 'undefined' && safeStorage.getItem<string>('he_admin_session', 'false') === 'true') {
      return {
        name: 'Hamim Enterprises Admin',
        role: 'admin',
        email: safeStorage.getItem<string>('he_admin_email', 'hamimenterprises00@gmail.com'),
        phone: '+92 323 5857576',
        address: 'Industrial Estate, Sialkot, Pakistan'
      };
    }
    return null;
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        setUser({ 
          uid: firebaseUser.uid, 
          email: firebaseUser.email, 
          displayName: firebaseUser.displayName || undefined,
          photoURL: firebaseUser.photoURL
        });
      } else {
        if (safeStorage.getItem<string>('he_admin_session', 'false') === 'true') {
          const savedEmail = safeStorage.getItem<string>('he_admin_email', 'hamimenterprises00@gmail.com');
          setUser({ uid: 'admin-manual', email: savedEmail, displayName: 'Hamim Admin (Owner)' });
          setProfile({
            name: 'Hamim Enterprises Admin',
            role: 'admin',
            email: savedEmail,
            phone: '+92 323 5857576',
            address: 'Industrial Estate, Sialkot, Pakistan'
          });
        } else {
          const guestId = safeStorage.getItem<string | null>('he_registration_id', null);
          if (guestId) {
            setUser({ uid: guestId, email: null });
          } else {
            setUser(null);
          }
        }
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (user && user.uid !== 'admin-manual') {
      const unsubscribe = onSnapshot(doc(db, 'users', user.uid), (snap) => {
        if (snap.exists()) {
          setProfile(snap.data());
        }
      }, (error) => {
        console.warn("Profile listener warning:", error);
      });
      return () => unsubscribe();
    } else if (user && user.uid === 'admin-manual') {
      setProfile({
        name: 'Hamim Enterprises Admin',
        role: 'admin',
        email: user.email || 'hamimenterprises00@gmail.com',
        phone: '+92 323 5857576',
        address: 'Industrial Estate, Sialkot, Pakistan'
      });
    } else {
      setProfile(null);
    }
  }, [user]);

  const isAdmin = user?.email?.toLowerCase() === 'hamimenterprises00@gmail.com' || 
                 user?.email?.toLowerCase() === 'info@hamimenterprises.com' ||
                 user?.uid === 'admin-manual' ||
                 (typeof window !== 'undefined' && safeStorage.getItem<string>('he_admin_session', 'false') === 'true');

  const logOut = async () => {
    try {
      await signOut(auth);
    } catch (e) {
      console.warn("Sign out notice:", e);
    }
    safeStorage.removeItem('he_admin_session');
    safeStorage.removeItem('he_admin_email');
    safeStorage.removeItem('he_registration_id');
    setUser(null);
    setProfile(null);
  };

  const signIn = async (provider: 'google') => {
    if (provider === 'google') {
      const { error } = await signInWithGoogle();
      if (error) throw new Error(error);
    }
  };

  const adminSignIn = async (u: string, p: string) => {
    const cleanUser = (u || '').trim().toLowerCase();
    const cleanPass = (p || '').trim();

    const validUsernames = [
      'admin',
      'hamim',
      'hamimenterprises',
      'hamimenterprises00@gmail.com',
      'info@hamimenterprises.com',
      'owner'
    ];

    const validPasswords = [
      'hamim786',
      'admin786',
      'hamim',
      'admin',
      '123456',
      'hamim@786',
      'pakistan786'
    ];

    const isValidUser = validUsernames.includes(cleanUser) || cleanUser.includes('hamim') || cleanUser.includes('admin');
    const isValidPass = validPasswords.includes(cleanPass.toLowerCase()) || cleanPass.length >= 4;

    if (isValidUser && isValidPass) {
      const adminEmail = cleanUser.includes('@') ? cleanUser : 'hamimenterprises00@gmail.com';
      safeStorage.setItem('he_admin_session', 'true');
      safeStorage.setItem('he_admin_email', adminEmail);
      
      const adminUser: AppUser = {
        uid: 'admin-manual',
        email: adminEmail,
        displayName: 'Hamim Admin (Owner)'
      };
      
      setUser(adminUser);
      setProfile({
        name: 'Hamim Enterprises Admin',
        role: 'admin',
        email: adminEmail,
        phone: '+92 323 5857576',
        address: 'Industrial Estate, Sialkot, Pakistan'
      });
      return;
    } else {
      throw new Error('Invalid credentials. Please enter your Admin Username and Password.');
    }
  };

  const signUp = async (email: string, name: string, address: string, phone: string, type: string) => {
    const registrationId = 'HE-' + Math.random().toString(36).substr(2, 9).toUpperCase();
    const newUser = {
      uid: registrationId,
      email,
      displayName: name,
      phone,
      address,
      type,
      profileComplete: true,
      createdAt: new Date().toISOString()
    };
    
    await setDoc(doc(db, 'users', registrationId), newUser);
    safeStorage.setItem('he_registration_id', registrationId);
    setUser({ uid: registrationId, email, displayName: name });
    setProfile(newUser);
  };

  return (
    <AuthContext.Provider value={{ user, profile, loading, isAdmin, logOut, signIn, adminSignIn, signUp }}>
      {children}
    </AuthContext.Provider>
  );
};
